import { groupDocuments } from '@/components/Account/Documents/utils';
import { evaluateDocStatus } from '@/components/CorporateUsers/modals/DocStatusIcon';

const CARRIER_WIDE_SLOTS = {
    AGREEMENT: { key: 'AGREEMENT', type: 'AGREEMENT', label: 'Master Service Agreement' },
    NDA: { key: 'NDA', type: 'NDA', label: 'Mutual NDA' },
    COI: { key: 'COI', type: 'COI', label: 'Certificate of Insurance' },
    WORKERS_COMP: { key: 'WORKERS_COMP', type: 'WORKERS_COMP', label: "Workers' Comp" },
    W9: { key: 'W9', type: 'W9', label: 'W-9' },
};

/** A document's compliance decision, by the review status it comes from. */
export const DECISIONS = {
    APPROVED: { label: 'Approved', tone: 'on', verb: 'Approve' },
    DENIED: { label: 'Denied', tone: 'bad', verb: 'Deny' },
};

/** The pill on a document no one in the org has decided on yet. */
export const AWAITING_REVIEW = { label: 'Awaiting review', tone: null };

// documents_compliance_rereview's rows: a document change that sent an approved carrier back for review.
const SENT_BACK = 'Sent back for review';

const rateSlotKeyOf = (shipperId) => `RATES:${shipperId}`;

const slotKeyOf = (doc) => {
    if (doc.type === 'RATES' && doc.partner_client_id) return rateSlotKeyOf(doc.partner_client_id);
    if (CARRIER_WIDE_SLOTS[doc.type] && !doc.partner_client_id) return doc.type;

    return null;
};

const byNewestFirst = (a, b) => new Date(b.created_at) - new Date(a.created_at);

const filledSlot = (slot, document) => {
    const { state, daysToExpiry } = evaluateDocStatus(document);

    return {
        ...slot,
        document: document ?? null,
        status: state,
        daysToExpiry,
        onFile: state === 'valid' || state === 'expiring',
    };
};

export const documentSlotsFor = ({ documents, linkedShippers, clientsById }) => {
    const newestFirst = [...documents].sort(byNewestFirst);
    const { currentBySlot, others, historyRows } = newestFirst.reduce(
        (buckets, doc) => {
            if (doc.archived) {
                buckets.historyRows.push(doc);
                return buckets;
            }
            if (!doc.link) return buckets;

            const slotKey = slotKeyOf(doc);
            if (!slotKey) {
                buckets.others.push(doc);
                return buckets;
            }
            if (buckets.currentBySlot[slotKey]) {
                buckets.historyRows.push(doc);
                return buckets;
            }

            buckets.currentBySlot[slotKey] = doc;

            return buckets;
        },
        { currentBySlot: {}, others: [], historyRows: [] }
    );

    const linkedShipperIds = new Set(linkedShippers.map((shipper) => shipper.client_id));
    const unlinkedAddendumShippers = Object.values(currentBySlot).reduce((shippers, doc) => {
        if (doc.type !== 'RATES' || linkedShipperIds.has(doc.partner_client_id)) return shippers;

        shippers.push({
            client_id: doc.partner_client_id,
            business_name: clientsById[doc.partner_client_id]?.business_name || doc.partner_client_id,
        });

        return shippers;
    }, []);
    const rateSlots = [...linkedShippers, ...unlinkedAddendumShippers].map((shipper) => {
        const key = rateSlotKeyOf(shipper.client_id);
        const slot = {
            key,
            type: 'RATES',
            label: 'Rate Addendum',
            partnerClientId: shipper.client_id,
            shipperName: shipper.business_name,
        };

        return filledSlot(slot, currentBySlot[key]);
    });

    const datedHistory = historyRows.map((doc) => ({ ...doc, effectiveDate: doc.signed_on || doc.created_at }));
    datedHistory.sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate));

    return {
        contracts: [
            filledSlot(CARRIER_WIDE_SLOTS.AGREEMENT, currentBySlot.AGREEMENT),
            ...rateSlots,
            filledSlot(CARRIER_WIDE_SLOTS.NDA, currentBySlot.NDA),
            filledSlot(CARRIER_WIDE_SLOTS.W9, currentBySlot.W9),
        ],
        insurance: [
            filledSlot(CARRIER_WIDE_SLOTS.COI, currentBySlot.COI),
            filledSlot(CARRIER_WIDE_SLOTS.WORKERS_COMP, currentBySlot.WORKERS_COMP),
        ],
        others,
        history: groupDocuments(datedHistory),
    };
};

/**
 * The latest decision on each document, by document_id. `reviews` is newest first, as GET_CARRIER_COMPLIANCE_REVIEWS
 * loads them.
 *
 * @returns {Object<string, object>}
 */
export const latestDecisionByDocument = (reviews) =>
    reviews.reduce((byDocument, review) => {
        if (review.document_id && !byDocument[review.document_id]) byDocument[review.document_id] = review;

        return byDocument;
    }, {});

// The slot label a document sits under on this page, or its own name when it fits no slot.
const documentLabel = (document, clientsById) => {
    if (!document) return 'All documents';
    if (document.type === 'RATES' && document.partner_client_id) {
        const shipperName = clientsById[document.partner_client_id]?.business_name;

        return shipperName ? `Rate Addendum — ${shipperName}` : 'Rate Addendum';
    }

    return CARRIER_WIDE_SLOTS[document.type]?.label || document.name || 'Document';
};

/**
 * One Compliance History row per review, newest first: the document it's on ("All documents" for a carrier-wide
 * decision), the decision, the note and who made it.
 */
export const complianceHistoryRows = (reviews, clientsById = {}) =>
    reviews.map((review) => ({
        review_id: review.review_id,
        reviewedAt: review.reviewed_at,
        document: documentLabel(review.document, clientsById),
        decision: DECISIONS[review.status]?.label || SENT_BACK,
        tone: DECISIONS[review.status]?.tone ?? null,
        note: review.note || '',
        reviewer: review.reviewer?.username || review.reviewer?.email || '',
    }));
